/**
 * The Hebrew for the fixed English sentences the server writes into a feedback note's `text` (and into the `error_location` and
 * `hint` rungs, which reuse them). Sources, all in `apps/server/src/homework/feedback.ts`: `NON_VERDICT_TEXT`, `DISPUTE_TEXT`,
 * `ON_REQUEST_TEXT`, `CHECK_COVERAGE` (the ✓ sentences), `ERROR_LOCATION_TEXT` and `HINT_TEXT`.
 *
 * These say what was checked, so a translation here is exactly as strong as its source. The map is keyed by the exact English;
 * a sentence with no entry is shown as received, never dropped. Text that quotes the learner's work or a course rule, or that
 * the model wrote (a solution), is untrusted: it is never passed through here. `note-sentences.test.ts` fails when a source
 * sentence has no entry.
 */

import type { UiLocale } from './locale.js';

export const NOTE_SENTENCES_HE: Readonly<Record<string, string>> = {
  // NON_VERDICT_TEXT
  'Read, not checked.': 'נקרא, לא נבדק.',
  'Read, not checked: the check needs a course rule the course materials have not been shown to state.':
    'נקרא, לא נבדק: הבדיקה דורשת כלל מהקורס, ולא הוצג שחומרי הקורס קובעים אותו.',
  'Read, not checked: this line could not be decided.': 'נקרא, לא נבדק: לא ניתן היה להכריע לגבי השורה הזאת.',
  'Read, not checked: the stored check could not be verified again, so it is not shown.':
    'נקרא, לא נבדק: לא ניתן היה לאמת שוב את הבדיקה השמורה, ולכן היא לא מוצגת.',
  // DISPUTE_TEXT
  'Under review: you said this verdict looks wrong. It will be reviewed by the date shown.':
    'בבדיקה חוזרת: דווח שהקביעה הזאת נראית שגויה. היא תיבדק עד התאריך המוצג.',
  'Reviewed: this is right. The check was wrong here.': 'נבדק שוב: זה נכון. הבדיקה טעתה כאן.',
  // ON_REQUEST_TEXT
  'Checked. Open the feedback to see it; any help it shows is recorded.':
    'נבדק. פתחו את המשוב כדי לראות אותו. כל עזרה שהוא מציג נרשמת.',
  // CHECK_COVERAGE (the ✓ sentences)
  'Each line of this chain equals the line before it.': 'כל שורה בשרשרת הזאת שווה לשורה שלפניה.',
  'The values you gave break the claim as you wrote it.': 'הערכים שניתנו סותרים את הטענה כפי שנכתבה.',
  'The values you gave satisfy the condition as you wrote it.': 'הערכים שניתנו מקיימים את התנאי כפי שנכתב.',
  'The two sets you wrote are equal.': 'שתי הקבוצות שנכתבו שוות.',
  // ERROR_LOCATION_TEXT
  'This line does not equal the line before it.': 'השורה הזאת אינה שווה לשורה שלפניה.',
  'The values you gave do not break the claim as you wrote it.': 'הערכים שניתנו אינם סותרים את הטענה כפי שנכתבה.',
  'The values you gave do not satisfy the condition as you wrote it.': 'הערכים שניתנו אינם מקיימים את התנאי כפי שנכתב.',
  'The two sets you wrote are not equal.': 'שתי הקבוצות שנכתבו אינן שוות.',
  // HINT_TEXT
  'Put the same number in for each letter in this line and in the line before it, and compare the two results.':
    'הציבו את אותו מספר במקום כל אות בשורה הזאת ובשורה שלפניה, והשוו בין שתי התוצאות.',
  'Put your values into each condition before the arrow and into each condition after it: a counterexample makes every condition before the arrow hold and at least one after it fail.':
    'הציבו את הערכים בכל תנאי שלפני החץ ובכל תנאי שאחריו: דוגמה נגדית גורמת לכל התנאים שלפני החץ להתקיים ולפחות לאחד מהתנאים שאחריו להיכשל.',
  'Put your values into each condition, one at a time, and check that every one of them holds.':
    'הציבו את הערכים בכל תנאי, אחד אחד, ובדקו שכולם מתקיימים.',
  'Compare the two sets one element at a time, in both directions: is each element of the left set in the right one, and the other way round?':
    'השוו בין שתי הקבוצות איבר אחר איבר, בשני הכיוונים: האם כל איבר בקבוצה השמאלית נמצא בימנית, ולהפך?',
};

/** A fixed server sentence in the learner's language; anything else (an unknown sentence) comes back as it came. */
export function localizedNoteSentence(text: string, locale: UiLocale): string {
  return locale === 'he' ? (NOTE_SENTENCES_HE[text] ?? text) : text;
}
